/**
 * System information and version management service
 */
import api, {
  MutationRequestOptions,
  getMutationRequestConfig,
} from '../utils/axiosConfig';

export interface MigrationLog {
  id: number;
  revision: string;
  version: string;
  description: string | null;
  applied_at: string;
  applied_by: string | null;
  execution_time_ms: number | null;
  status: string;
}

export interface SystemVersion {
  database_version: string;
  latest_migration: MigrationLog | null;
  status: string;
}

export interface HealthStatus {
  status: string;
  database: string;
  version: string;
  error?: string;
}

export interface PackagePricingSetting {
  deviceFee: number;
  cloudPlatformFee: number;
}

export interface HardwareVersionSetting {
  key: string;
  name: string;
  description?: string | null;
  featureKeys: string[];
  deviceFee: number;
  cloudPlatformFee: number;
  displayOrder: number;
}

export interface PricingSettings {
  foundation: PackagePricingSetting;
  recognition: PackagePricingSetting;
  insight: PackagePricingSetting;
  hardwareVersions?: HardwareVersionSetting[];
  reminders: {
    lureExpireDays: number;
    lureValidityDays: number;
    feeDueReminderDays: number;
  };
}

/**
 * Get current system version information
 */
export const getSystemVersion = async (): Promise<SystemVersion> => {
  const response = await api.get('/system/version');
  return response.data;
};

/**
 * Get migration history
 */
export const getMigrationHistory = async (limit: number = 10): Promise<MigrationLog[]> => {
  const response = await api.get('/system/migrations', {
    params: { limit }
  });
  return response.data;
};

/**
 * Get migrations by version
 */
export const getMigrationsByVersion = async (version: string): Promise<MigrationLog[]> => {
  const response = await api.get(`/system/migrations/${version}`);
  return response.data;
};

/**
 * System health check
 */
export const getHealthStatus = async (): Promise<HealthStatus> => {
  const response = await api.get('/system/health');
  return response.data;
};

/**
 * Get device version pricing settings.
 */
export const getPricingSettings = async (): Promise<PricingSettings> => {
  const response = await api.get('/system/pricing-settings');
  return response.data;
};

/**
 * Update device version pricing settings.
 */
export const updatePricingSettings = async (
  settings: PricingSettings,
  options?: MutationRequestOptions,
): Promise<PricingSettings> => {
  const response = await api.put(
    '/system/pricing-settings',
    settings,
    getMutationRequestConfig(options),
  );
  return response.data;
};
